// =============================================================================
// AddressAutocomplete — address typeahead (Google Places, OSM fallback)
// =============================================================================
// Drop-in replacement for the address <Input>. When VITE_GOOGLE_MAPS_API_KEY
// is set we use GOOGLE PLACES autocomplete — the SAME Google Maps that powers
// the "Pin on map" picker — so the dropdown, geocoding and coverage match the
// map. Without a key we fall back to OpenStreetMap's free Nominatim typeahead
// so the app still works offline of Google.
//
// Either way: selecting a suggestion fills the address text AND propagates
// city + lat/lng to the parent via onPick. The public props are unchanged, so
// every call site (customer form, etc.) is untouched.

import { type ComponentType, useEffect, useRef, useState } from 'react';
import {
  Autocomplete as AutocompleteBase,
  useJsApiLoader,
  type AutocompleteProps,
} from '@react-google-maps/api';
import { Input } from './Input';
import { cn } from '../lib/cn';

// @react-google-maps/api ships class components that TS 5 + @types/react 18.3
// reject as JSX elements ("no 'props' property"). Re-type as a functional
// component via its exported props type — compile-time-only, identical at
// runtime. (Same treatment as LocationPicker.)
const Autocomplete = AutocompleteBase as unknown as ComponentType<AutocompleteProps>;

const GOOGLE_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
// Module-level so the reference is stable across renders (useJsApiLoader warns
// otherwise) and matches the LocationPicker's loader options exactly — same id
// + libraries means the Maps script is loaded once and shared.
const GOOGLE_LIBS: 'places'[] = ['places'];

export interface AddressPick {
  address: string;
  city: string | null;
  latitude: number;
  longitude: number;
}

/**
 * Drop the redundant trailing country (and any postcode that comes with it)
 * from a formatted address — the city is captured in its own field, and we
 * don't want "Mauritius" tacked onto every address line. Returns '' for empty
 * input so callers can fall through to their next option with `||`.
 */
function stripCountry(addr?: string | null): string {
  if (!addr) return '';
  return addr.replace(/,?\s*\d{0,5}\s*Mauritius\s*$/i, '').trim();
}

interface Props {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  /**
   * Fired when the user picks a suggestion. Parents can use this to fill the
   * city + lat/lng fields. Optional — if omitted, only the address text is set.
   */
  onPick?: (pick: AddressPick) => void;
  /** Country bias. Default 'mu' (Mauritius). */
  countryCode?: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
}

/**
 * Public component — routes to Google Places when a key is configured,
 * otherwise the Nominatim fallback. Same props for both.
 */
export function AddressAutocomplete(props: Props): JSX.Element {
  return GOOGLE_KEY ? <GoogleAddress {...props} /> : <NominatimAddress {...props} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Google Places implementation
// ─────────────────────────────────────────────────────────────────────────────
function GoogleAddress({
  label = 'Address',
  value,
  onChange,
  onPick,
  countryCode = 'mu',
  placeholder = 'Start typing an address…',
  className,
  required,
}: Props): JSX.Element {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_KEY ?? '',
    libraries: GOOGLE_LIBS,
  });
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const onPlaceChanged = (): void => {
    const place = acRef.current?.getPlace();
    if (!place) return;
    const comp = place.address_components ?? [];
    const get = (type: string): string | undefined =>
      comp.find((c) => c.types.includes(type))?.long_name;

    // City: OSM/Google use different admin levels depending on the place.
    const city =
      get('locality') ??
      get('postal_town') ??
      get('administrative_area_level_2') ??
      get('administrative_area_level_1') ??
      null;

    // Keep the FULL address the user selected. We use Google's formatted_address
    // (the complete human-readable line) and only strip the redundant country
    // tail, since the city is captured separately. Rebuilding from individual
    // street_number/route components collapsed sparse MU addresses down to a
    // single fragment, so we prefer formatted_address and only fall back to the
    // component build / place name when it's missing.
    const streetLine = [get('street_number'), get('route')].filter(Boolean).join(' ');
    const suburb = get('sublocality') ?? get('neighborhood');
    const street =
      stripCountry(place.formatted_address) ||
      [streetLine || null, suburb ?? null].filter(Boolean).join(', ') ||
      place.name ||
      value;

    onChange(street);
    const loc = place.geometry?.location;
    if (loc) onPick?.({ address: street, city, latitude: loc.lat(), longitude: loc.lng() });
  };

  // Plain input until the Maps JS is ready, so typing still works during load.
  const field = (
    <Input
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      autoComplete="off"
    />
  );

  return (
    <div className={cn('relative', className)}>
      {isLoaded ? (
        <Autocomplete
          onLoad={(ac) => {
            acRef.current = ac;
            ac.setComponentRestrictions({ country: countryCode });
            ac.setFields(['geometry', 'formatted_address', 'address_components', 'name']);
          }}
          onPlaceChanged={onPlaceChanged}
        >
          {field}
        </Autocomplete>
      ) : (
        field
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Nominatim / OpenStreetMap fallback (no Google key)
// ─────────────────────────────────────────────────────────────────────────────
interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    road?: string;
    house_number?: string;
    suburb?: string;
    village?: string;
    town?: string;
    city?: string;
    county?: string;
    state?: string;
  };
}

/**
 * Best-effort city extraction from a Nominatim result. OSM uses different
 * keys depending on the place type (village vs town vs city, etc.).
 */
function pickCity(a: NominatimResult['address']): string | null {
  if (!a) return null;
  return a.city ?? a.town ?? a.village ?? a.suburb ?? a.county ?? null;
}

/**
 * Build the address string for the address field. We keep the FULL display
 * name the user selected and only strip the redundant country tail, since the
 * city is captured separately. Rebuilding from house_number/road components
 * collapsed sparse MU addresses down to a single fragment.
 */
function pickStreetAddress(r: NominatimResult): string {
  return stripCountry(r.display_name) || r.display_name.trim();
}

function NominatimAddress({
  label = 'Address',
  value,
  onChange,
  onPick,
  countryCode = 'mu',
  placeholder = 'Start typing an address…',
  className,
  required,
}: Props): JSX.Element {
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  // Track whether the latest text change was from a suggestion pick so we
  // don't re-query Nominatim with the freshly-filled value.
  const justPickedRef = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return;
    }
    const term = value.trim();
    if (term.length < 3) {
      setSuggestions([]);
      setBusy(false);
      return;
    }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const u = new URL('https://nominatim.openstreetmap.org/search');
        u.searchParams.set('format', 'json');
        u.searchParams.set('q', term);
        u.searchParams.set('countrycodes', countryCode);
        u.searchParams.set('limit', '6');
        u.searchParams.set('addressdetails', '1');
        const r = await fetch(u.toString(), { headers: { Accept: 'application/json' } });
        const j = (await r.json()) as NominatimResult[];
        setSuggestions(j);
        setOpen(j.length > 0);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setBusy(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value, countryCode]);

  // Close on click outside
  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pickSuggestion = (r: NominatimResult): void => {
    const street = pickStreetAddress(r);
    justPickedRef.current = true;
    onChange(street);
    onPick?.({
      address: street,
      city: pickCity(r.address),
      latitude: Number(r.lat),
      longitude: Number(r.lon),
    });
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <Input
        label={label}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open && e.target.value.trim().length >= 3) setOpen(true);
        }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />
      {open && (suggestions.length > 0 || busy) && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-surface border border-rule rounded-md shadow-lg max-h-64 overflow-y-auto"
          role="listbox"
        >
          {busy && suggestions.length === 0 && (
            <div className="px-3 py-2 text-xs text-ink-3 italic">Searching…</div>
          )}
          {suggestions.map((s, i) => (
            <button
              key={`${s.lat}-${s.lon}-${i}`}
              type="button"
              onClick={() => pickSuggestion(s)}
              onMouseEnter={() => setActiveIdx(i)}
              className={cn(
                'block w-full text-left px-3 py-2 text-sm border-b border-rule-2 last:border-b-0 transition',
                i === activeIdx
                  ? 'bg-paper-2 text-ink'
                  : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
              )}
              role="option"
              aria-selected={i === activeIdx}
            >
              {s.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
